import { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { internalLayerVersionsKeys } from './queryKeys';
import type { Location } from 'types/locations';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';
import type { InternalLayer } from 'hooks/internalLayer/types';

export type InternalLayerVersionWithFeatures = {
  internalLayer: InternalLayer;
  featuresQuery: UseQueryResult<Location[], Error>;
  version?: InternalLayerVersion;
};

async function fetchInternalLayerVersionFeatures(id: string) {
  const resp = await axios.get<Location[]>(
    `${config.apiGateway.ics_locations}/by-version/${id}`
  );
  return resp.data;
}

/** Fetch features for a single internal layer version by its id. */
export function useListInternalLayerVersionFeaturesQuery(
  id: string | undefined
) {
  return useQuery({
    queryKey: internalLayerVersionsKeys.features(id ?? ''),
    queryFn: () => fetchInternalLayerVersionFeatures(id!),
    enabled: !!id,
  });
}

/**
 * Load internal layers (optionally filtered by incident and AORs) and the
 * internal_layer_version to show per internal_layer. Use version selected in
 * the sidebar, otherwise use draft version.
 */
export function useInternalLayersFeatures(
  incidentId?: string | null,
  aorIds?: string[] | null,
  selectedInternalLayerVersionByInternalLayerId?: Record<
    string,
    InternalLayerVersion
  >
) {
  const groupGuid = useGroupGuid();

  const { data: layersData, isLoading: isLoadingLayers } =
    useListInternalLayersQuery({
      filter: {
        groupGuid: groupGuid,
        ...(incidentId ? { incidentId } : {}),
        ...(aorIds && aorIds.length ? { aorIds } : {}),
      },
    });

  const layers = useMemo(() => layersData?.data ?? [], [layersData]);

  const { versionIds, versionIdToEntry } = useMemo(() => {
    const entryMap = new Map<
      string,
      { internalLayer: InternalLayer; version?: InternalLayerVersion }
    >();

    for (const layer of layers) {
      const selectedVersion =
        selectedInternalLayerVersionByInternalLayerId?.[layer.id];
      const versionId = selectedVersion?.id ?? layer.draftVersionId;
      if (versionId) {
        entryMap.set(versionId, {
          internalLayer: layer,
          version: selectedVersion,
        });
      }
    }

    return {
      versionIds: [...entryMap.keys()],
      versionIdToEntry: entryMap,
    };
  }, [layers, selectedInternalLayerVersionByInternalLayerId]);

  const featureQueriesArray = useQueries({
    queries: versionIds.map((id) => ({
      queryKey: internalLayerVersionsKeys.features(id),
      queryFn: () => fetchInternalLayerVersionFeatures(id),
    })),
  });

  const internalLayerVersionsMap = useMemo(() => {
    const map = new Map<string, InternalLayerVersionWithFeatures>();
    versionIds.forEach((versionId, i) => {
      const entry = versionIdToEntry.get(versionId);
      if (!entry) return;
      map.set(versionId, {
        ...entry,
        featuresQuery: featureQueriesArray[i],
      });
    });
    return map;
  }, [versionIds, versionIdToEntry, featureQueriesArray]);

  const isLoadingFeatures = featureQueriesArray.some((q) => q.isLoading);

  return {
    internalLayerVersionsMap,
    isLoading: isLoadingLayers || isLoadingFeatures,
  };
}
